// Timezone monitor and diagnostics.
//
// Roava's storage and math layers are already absolute (UTC ISO + epoch ms).
// This module adds the diagnostic surface required to *prove* that and to
// notice device-timezone changes without taking any side effects.
//
// Strict rules:
//   - No auth, cache, queue, or tracking side effects.
//   - All logs are throttled to avoid Xcode console flooding during active
//     tracking (1 Hz UI tick × N rows would otherwise spam).
//
// OTA-safe: pure JS.

const POLL_MS = 60_000;
const TRACKING_LOG_THROTTLE_MS = 30_000;
const DISPLAY_LOG_THROTTLE_MS = 60_000;

let initialized = false;
let lastTz: string | null = null;
const lastLoggedAt = new Map<string, number>();

function currentTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
  } catch {
    return "unknown";
  }
}

function shouldLog(key: string, throttleMs: number): boolean {
  const now = Date.now();
  const prev = lastLoggedAt.get(key) ?? 0;
  if (now - prev < throttleMs) return false;
  lastLoggedAt.set(key, now);
  return true;
}

/** Initialize the monitor once. Safe to call multiple times. */
export function initTimezoneMonitor(): void {
  if (initialized) return;
  initialized = true;
  lastTz = currentTz();
  // eslint-disable-next-line no-console
  console.log(`[timezone] device timezone=${lastTz}`);

  const check = () => {
    const tz = currentTz();
    if (tz !== lastTz) {
      const from = lastTz;
      lastTz = tz;
      // eslint-disable-next-line no-console
      console.log(`[timezone] timezone changed from=${from} to=${tz} decision=display-only`);
    }
  };

  if (typeof window !== "undefined") {
    window.setInterval(check, POLL_MS);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    }
    window.addEventListener("focus", check);
  }
}

/**
 * Log that a ping is being stored with an absolute UTC timestamp. Throttled
 * so the 1+ Hz tracking loop doesn't spam Xcode.
 */
export function logTrackingTimestamp(capturedAt: string): void {
  if (!shouldLog("tracking-ts", TRACKING_LOG_THROTTLE_MS)) return;
  const epochMs = Date.parse(capturedAt);
  // eslint-disable-next-line no-console
  console.log(
    `[timezone] tracking timestamp source=utc capturedAt=${capturedAt} epochMs=${Number.isFinite(epochMs) ? epochMs : "invalid"}`,
  );
}

/** Log that a drain batch has been verified to be in capturedAt order. */
export function logQueueOrderOk(count: number): void {
  // Drains are infrequent (network edge / user action), so no throttle.
  // eslint-disable-next-line no-console
  console.log(`[timezone] queued ping order ok count=${count}`);
}

/**
 * Log a display-only conversion of a stored UTC timestamp into a local string.
 * Throttled per-key (e.g. "last-gps", "tracking-started") so 1s UI ticks don't
 * flood the console.
 */
export function logDisplayLocalTime(key: string, localStr: string, sourceUtc: string): void {
  if (!shouldLog(`display-${key}`, DISPLAY_LOG_THROTTLE_MS)) return;
  // eslint-disable-next-line no-console
  console.log(`[timezone] display local time=${localStr} sourceUtc=${sourceUtc}`);
}
